import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Business as BusinessIcon, Check as CheckIcon, Star as StarIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface UnitSwitcherProps {
  expanded?: boolean;
}

const UnitSwitcher: React.FC<UnitSwitcherProps> = ({ expanded = true }) => {
  const { currentUnit, availableUnits, switchUnit } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);

  if (!currentUnit) return null;
  const hasMultiple = (availableUnits?.length || 0) > 1;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!hasMultiple) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (unitId: string) => {
    handleClose();
    if (unitId === currentUnit.id) return;
    setSwitching(true);
    try {
      await switchUnit(unitId);
      // Recarrega a aplicação para garantir que listas/caches sejam refeitos
      // com o novo unit_id (axios passa a enviar o novo X-Unit-Id).
      window.location.reload();
    } catch (err: any) {
      console.error('switchUnit error:', err);
      alert(err?.message || 'Erro ao trocar unidade.');
      setSwitching(false);
    }
  };

  if (!expanded) {
    return (
      <Tooltip title={`Unidade: ${currentUnit.name}${hasMultiple ? ' (clique para trocar)' : ''}`} placement="right">
        <span>
          <Button
            size="small"
            onClick={handleOpen}
            disabled={!hasMultiple || switching}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              borderRadius: '50%',
              p: 0,
              color: '#0d9488',
              bgcolor: 'rgba(20,184,166,0.10)',
              '&:hover': { bgcolor: 'rgba(20,184,166,0.18)' },
            }}
          >
            {switching ? <CircularProgress size={16} /> : <BusinessIcon sx={{ fontSize: 18 }} />}
          </Button>
          {anchorEl && renderMenu()}
        </span>
      </Tooltip>
    );
  }

  function renderMenu() {
    return (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        {(availableUnits || []).map((u) => {
          const isCurrent = u.id === currentUnit?.id;
          return (
            <MenuItem key={u.id} selected={isCurrent} onClick={() => handleSelect(u.id)}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                {isCurrent ? <CheckIcon fontSize="small" sx={{ color: '#0d9488' }} /> : <BusinessIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
              </ListItemIcon>
              <ListItemText
                primary={u.name}
                secondary={u.short_name}
                primaryTypographyProps={{ fontWeight: isCurrent ? 700 : 500, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.7rem' }}
              />
              {u.is_matrix && (
                <Chip
                  label="Matriz"
                  size="small"
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  sx={{ ml: 1, height: 20, fontSize: '0.65rem', bgcolor: '#fef9c3', color: '#854d0e' }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
      <Button
        fullWidth
        size="small"
        startIcon={switching ? <CircularProgress size={14} /> : <BusinessIcon sx={{ fontSize: 18 }} />}
        onClick={handleOpen}
        disabled={!hasMultiple || switching}
        sx={{
          textTransform: 'none',
          justifyContent: 'flex-start',
          color: '#0f172a',
          bgcolor: 'rgba(20,184,166,0.08)',
          border: '1px solid rgba(20,184,166,0.25)',
          '&:hover': { bgcolor: 'rgba(20,184,166,0.14)' },
          py: 0.6,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
          <Box sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {currentUnit.name}
          </Box>
          <Box sx={{ fontSize: '0.65rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {currentUnit.is_matrix && <StarIcon sx={{ fontSize: 11, color: '#ca8a04' }} />}
            {currentUnit.is_matrix ? 'Matriz' : 'Filial'} · {currentUnit.short_name}
          </Box>
        </Box>
      </Button>
      {renderMenu()}
    </Box>
  );
};

export default UnitSwitcher;
